import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import { modifier } from 'ember-modifier';
import { guidFor } from '@ember/object/internals';
import { concat } from '@ember/helper';
import { debounce } from '@ember/runloop';
import { PhMagnifyingGlass, PhX } from 'ember-phosphor-icons';
import { and, not } from 'tio-ui/utilities';
import FormField from './form-field';

const DEFAULT_DEBOUNCE_MS = 300;

// ---------------------------------------------------------------------------
// SearchInput – reimagined search-specific input with icon + clear button
// ---------------------------------------------------------------------------

export type SearchInputSize = 'sm' | 'md' | 'lg';

interface SearchInputSignature {
  Args: {
    /** Current search value */
    value?: string;
    /** Called on every keystroke with the new value (debounced by default) */
    onChange?: (value: string) => void;
    /** Called when the user presses Enter */
    onSearch?: (value: string) => void;
    /** Called when the clear button is clicked */
    onClear?: () => void;

    /** Label displayed above the input */
    label?: string;
    /** Hint text */
    hint?: string;
    /** Placeholder text @default "Search…" */
    placeholder?: string;
    /** Size variant @default "md" */
    size?: SearchInputSize;
    /** Disable the input */
    isDisabled?: boolean;
    /** Debounce delay in ms for @onChange. Set to 0 for immediate. @default 300 */
    debounce?: number;
  };
  Element: HTMLInputElement;
}

// ---------------------------------------------------------------------------
// Size classes
// ---------------------------------------------------------------------------

/**
 * Per-size visual constants. Theme (bg/color/border/focus ring) lives in
 * the .app-input semantic class; only icon positioning and the
 * leading/trailing icon-padding utilities are size-specific here.
 */
const SIZE_CLASSES: Record<
  SearchInputSize,
  { iconPadding: string; iconSize: number; iconLeft: string; clearRight: string }
> = {
  sm: { iconPadding: 'pl-8 pr-8', iconSize: 14, iconLeft: 'left-2', clearRight: 'right-1.5' },
  md: { iconPadding: 'pl-10 pr-10', iconSize: 16, iconLeft: 'left-3', clearRight: 'right-2.5' },
  lg: { iconPadding: 'pl-11 pr-11', iconSize: 18, iconLeft: 'left-3.5', clearRight: 'right-3' },
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

/**
 * Reimagined SearchInput — a search-specific input with magnifying glass icon
 * and clear button.
 *
 * Fires `@onChange` on every keystroke and `@onSearch` on Enter.
 * The clear button appears when there's a value and fires `@onClear`.
 *
 * @example Basic usage
 * ```gts
 * import SearchInput from 'tio-ui/components/search-input';
 *
 * <SearchInput
 *   @value={{this.query}}
 *   @onChange={{this.setQuery}}
 *   @onSearch={{this.performSearch}}
 *   @placeholder="Search employees…"
 * />
 * ```
 *
 * @example With label
 * ```gts
 * <SearchInput
 *   @label="Find a program"
 *   @value={{this.programQuery}}
 *   @onChange={{this.setProgramQuery}}
 *   @onClear={{this.clearProgramSearch}}
 * />
 * ```
 */
export default class SearchInput extends Component<SearchInputSignature> {
  private _fieldId = `reimagined-search-${guidFor(this)}`;

  // Sync the DOM value from @value only when the input is NOT focused,
  // so debounced route refreshes don't clobber what the user is typing.
  syncValue = modifier((el: HTMLInputElement, [value]: [string | undefined]) => {
    if (document.activeElement !== el) {
      el.value = value ?? '';
    }
  });

  private get size(): SearchInputSize {
    return this.args.size ?? 'md';
  }

  private get sizeClasses() {
    return SIZE_CLASSES[this.size];
  }

  private get placeholder(): string {
    return this.args.placeholder ?? 'Search…';
  }

  private get hasValue(): boolean {
    return Boolean(this.args.value?.length);
  }

  private get inputClasses(): string {
    // Theme + states owned by .app-input (in addons/tio-reimagined/src/styles.css).
    // Icon padding stays inline because it varies per size for this component.
    const classes = [
      'app-input',
      `app-input--${this.size}`,
      this.sizeClasses.iconPadding,
      '[&::-webkit-search-cancel-button]:hidden',
      '[&::-webkit-search-decoration]:hidden',
    ];
    return classes.join(' ');
  }

  private get debounceMs() {
    return this.args.debounce ?? DEFAULT_DEBOUNCE_MS;
  }

  private _fireOnChange = (value: string) => {
    this.args.onChange?.(value);
  };

  handleInput = (event: Event) => {
    const input = event.target as HTMLInputElement;
    const delay = this.debounceMs;

    if (delay > 0) {
      debounce(this, this._fireOnChange, input.value, delay);
    } else {
      this.args.onChange?.(input.value);
    }
  };

  handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const value = (event.target as HTMLInputElement).value;
      this.args.onSearch?.(value);
    }
  };

  handleClear = () => {
    this.args.onChange?.('');
    this.args.onClear?.();
  };

  <template>
    <FormField
      @label={{@label}}
      @hint={{@hint}}
      @isDisabled={{@isDisabled}}
      @size={{this.size}}
      @fieldId={{this._fieldId}}
      as |field|
    >
      <div class="relative">
        {{! Search icon }}
        <span
          class="absolute
            {{this.sizeClasses.iconLeft}}
            top-1/2 -translate-y-1/2 text-[var(--text-tertiary)] pointer-events-none"
          aria-hidden="true"
        >
          <PhMagnifyingGlass @size={{this.sizeClasses.iconSize}} />
        </span>

        <input
          data-test-search-input
          id={{field.fieldId}}
          type="search"
          placeholder={{this.placeholder}}
          disabled={{@isDisabled}}
          {{this.syncValue @value}}
          aria-describedby={{if @hint (concat field.fieldId "-hint")}}
          class={{this.inputClasses}}
          {{on "input" this.handleInput}}
          {{on "keydown" this.handleKeyDown}}
          ...attributes
        />

        {{! Clear button — hidden when disabled }}
        {{#if (and this.hasValue (not @isDisabled))}}
          <button
            data-test-search-clear
            type="button"
            class="absolute
              {{this.sizeClasses.clearRight}}
              top-1/2 -translate-y-1/2 p-0.5 rounded text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-sunken)] transition-colors"
            aria-label="Clear search"
            {{on "click" this.handleClear}}
          >
            <PhX @size={{this.sizeClasses.iconSize}} />
          </button>
        {{/if}}
      </div>
    </FormField>
  </template>
}

export type { SearchInputSignature };
